import {
    Activity,
    FlaskConical,
    HeartPulse,
    Mail,
    MapPin,
    Pill,
    Ruler,
    ShieldAlert,
    ShieldCheck,
    Sparkles,
    Stethoscope,
    Users,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { formatValue } from '@/lib/format';
import { ECG_QUALITY_LABELS } from '@/lib/ecgStudies';
import { isUpcoming, type PatientCardData } from '@/components/medic/patientCardData';
import { PatientProfileBlock } from '@/components/medic/PatientProfileBlock';
import { PatientCriticalBar } from '@/components/medic/PatientCriticalBar';
import { PatientAmbulanceHistoryBlock } from '@/components/medic/PatientAmbulanceHistoryBlock';
import {
    collectionCount,
    summarizeRecords,
} from '@/components/medic/patientCardSummary';

export type PatientCardTab =
    | 'overview'
    | 'ecg'
    | 'psy'
    | 'appointments'
    | 'team'
    | 'referrals'
    | 'labs'
    | 'diagnoses'
    | 'prescriptions'
    | 'allergies'
    | 'measurements';

interface Props {
    data: PatientCardData;
    onGoTab: (tab: PatientCardTab) => void;
    onOpenStudy: (studyId: string) => void;
    onOpenAppointment: (appointmentId: string) => void;
    /** Открыть запись вызова скорой (документ ambulance_calls). */
    onOpenCall: (callId: string) => void;
}

function stateNote(collection: { state: string }): string {
    if (collection.state === 'ready') return '';
    if (collection.state === 'denied') return 'Нет доступа';
    if (collection.state === 'error') return 'Не удалось загрузить';
    return 'Раздел недоступен';
}

function joinTitles(items: Array<{ title: string }>, limit = 2): string {
    const titles = items.map((item) => item.title).filter(Boolean);
    if (titles.length === 0) return '';
    if (titles.length <= limit) return titles.join('; ');
    return `${titles.slice(0, limit).join('; ')} и ещё ${titles.length - limit}`;
}

function SummaryCard({
    icon,
    title,
    count,
    value,
    detail,
    note,
    actionLabel,
    onOpen,
    extra,
    alert,
}: {
    icon: React.ReactNode;
    title: string;
    count: number | null;
    value: string;
    detail?: string;
    note?: string;
    actionLabel: string;
    onOpen: () => void;
    /** Дополнительное быстрое действие (например, открыть последнюю запись). */
    extra?: React.ReactNode;
    alert?: boolean;
}) {
    return (
        <div className={`flex min-w-0 flex-col rounded-lg border bg-card p-3 ${alert ? 'border-destructive/40' : ''}`}>
            <div className="flex items-center gap-2 text-sm font-medium">
                <span className={alert ? 'text-destructive' : 'text-primary'}>{icon}</span>
                <span className="truncate">{title}</span>
                {count !== null && (
                    <span className="ml-auto rounded-full border bg-muted/40 px-2 py-0.5 text-[10px] text-muted-foreground">
                        {count}
                    </span>
                )}
            </div>
            {note ? (
                <div className="mt-1 text-xs text-muted-foreground">{note}</div>
            ) : (
                <>
                    <div className="mt-1 line-clamp-2 min-h-[2.25rem] text-sm">{value || 'Нет записей'}</div>
                    {detail && <div className="mt-0.5 text-[11px] text-muted-foreground">{detail}</div>}
                </>
            )}
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
                <Button size="sm" variant="outline" className="h-7 px-2 text-[11px]" onClick={onOpen}>
                    {actionLabel}
                </Button>
                {extra}
            </div>
        </div>
    );
}

function Detail({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
    return (
        <div className="flex items-start gap-2 rounded-lg border bg-card px-3 py-2">
            <span className="mt-0.5 text-muted-foreground">{icon}</span>
            <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
                <div className="whitespace-pre-wrap break-words text-sm">{value}</div>
            </div>
        </div>
    );
}

export function PatientCardOverview({ data, onGoTab, onOpenStudy, onOpenAppointment, onOpenCall }: Props) {
    const patient = data.patient;
    const records = data.records;
    const summary = summarizeRecords(records);
    const lastStudy = data.ecg.studies[0] || null;
    const lastResult = data.psy.results[0] || null;
    const lastConclusion = data.psy.conclusions[0] || null;
    const nextAppointment = data.appointments.items.find((item) => isUpcoming(item.appointmentAt)) || null;

    const teamNote = stateNote(records.careTeam);
    const attendingName = patient.attendingDoctorName || summary.attending?.title || 'не назначен';
    const activeTeamCount = records.careTeam.state === 'ready' ? summary.activeTeam.length : null;

    const diagnosesNote = stateNote(records.diagnoses);
    const prescriptionsNote = stateNote(records.prescriptions);
    const allergiesNote = stateNote(records.allergies);
    const labsNote = stateNote(records.labs);
    const measurementsNote = stateNote(records.measurements);

    const latestDiagnosis = summary.activeDiagnoses[0] || records.diagnoses.items[0] || null;
    const latestPrescription = summary.activePrescriptions[0] || records.prescriptions.items[0] || null;
    const latestMeasurement = summary.latestMeasurement;

    const contactItems = [
        patient.email ? <Detail key="email" icon={<Mail className="h-4 w-4" />} label="Email" value={patient.email} /> : null,
        patient.address ? <Detail key="address" icon={<MapPin className="h-4 w-4" />} label="Адрес" value={patient.address} /> : null,
        patient.snils ? <Detail key="snils" icon={<ShieldCheck className="h-4 w-4" />} label="СНИЛС" value={patient.snils} /> : null,
    ].filter(Boolean);

    return (
        <div className="space-y-4">
            {/* Компактно и выделенно наверху: аллергии, диагнозы, измерения, ЭКГ.
                Адрес/пол/год — в PatientProfileBlock ниже (не дублируем). */}
            <PatientCriticalBar data={data} onGoTab={onGoTab} onOpenStudy={onOpenStudy} />

            <section className="space-y-2">
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Клиническая картина</div>
                <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
                    <SummaryCard
                        icon={<Stethoscope className="h-4 w-4" />}
                        title="Диагнозы"
                        count={collectionCount(records.diagnoses)}
                        value={joinTitles(summary.activeDiagnoses)}
                        detail={
                            summary.activeDiagnoses.length > 0
                                ? `Активных: ${summary.activeDiagnoses.length}`
                                : latestDiagnosis
                                    ? `Последний: ${latestDiagnosis.title}`
                                    : ''
                        }
                        note={diagnosesNote}
                        actionLabel="Все диагнозы"
                        onOpen={() => onGoTab('diagnoses')}
                    />
                    <SummaryCard
                        icon={<Pill className="h-4 w-4" />}
                        title="Назначения"
                        count={collectionCount(records.prescriptions)}
                        value={joinTitles(summary.activePrescriptions)}
                        detail={
                            summary.activePrescriptions.length > 0
                                ? `Активных: ${summary.activePrescriptions.length}`
                                : latestPrescription
                                    ? `Последнее: ${latestPrescription.title}`
                                    : ''
                        }
                        note={prescriptionsNote}
                        actionLabel="Все назначения"
                        onOpen={() => onGoTab('prescriptions')}
                    />
                    <SummaryCard
                        icon={<ShieldAlert className="h-4 w-4" />}
                        title="Аллергии"
                        count={collectionCount(records.allergies)}
                        value={joinTitles(summary.activeAllergies)}
                        detail={summary.activeAllergies.length > 0 ? 'Есть активные аллергии' : 'Активных аллергий нет'}
                        note={allergiesNote}
                        actionLabel="Все аллергии"
                        onOpen={() => onGoTab('allergies')}
                        alert={summary.activeAllergies.length > 0}
                    />
                    <SummaryCard
                        icon={<FlaskConical className="h-4 w-4" />}
                        title="Анализы"
                        count={collectionCount(records.labs)}
                        value={summary.latestLab ? summary.latestLab.title : 'Результатов нет'}
                        detail={summary.latestLab ? summary.latestLab.date : ''}
                        note={labsNote}
                        actionLabel="Все анализы"
                        onOpen={() => onGoTab('labs')}
                    />
                </div>
            </section>

            <section className="space-y-2">
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Наблюдение и команда</div>
                <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
                    <SummaryCard
                        icon={<Users className="h-4 w-4" />}
                        title="Команда"
                        count={activeTeamCount}
                        value={`Лечащий врач: ${attendingName}`}
                        detail={summary.activeTeam.length > 0 ? `В команде: ${summary.activeTeam.length}` : ''}
                        note={teamNote}
                        actionLabel="Открыть команду"
                        onOpen={() => onGoTab('team')}
                    />
                    <SummaryCard
                        icon={<Ruler className="h-4 w-4" />}
                        title="Измерения"
                        count={collectionCount(records.measurements)}
                        value={latestMeasurement ? latestMeasurement.title : 'Измерений нет'}
                        detail={latestMeasurement ? latestMeasurement.date : ''}
                        note={measurementsNote}
                        actionLabel="Все измерения"
                        onOpen={() => onGoTab('measurements')}
                    />
                    <SummaryCard
                        icon={<HeartPulse className="h-4 w-4" />}
                        title="Приёмы"
                        count={data.appointments.state === 'ready' ? data.appointments.items.length : null}
                        value={nextAppointment ? formatValue(nextAppointment.appointmentAt, '', 'timestamp') : 'Ближайшая запись не назначена'}
                        detail={nextAppointment ? 'Ближайший приём' : ''}
                        note={data.appointments.state === 'ready'
                            ? ''
                            : data.appointments.state === 'denied'
                                ? 'Нет доступа'
                                : 'Раздел недоступен'}
                        actionLabel="Все приёмы"
                        onOpen={() => onGoTab('appointments')}
                        extra={nextAppointment && (
                            <Button
                                size="sm"
                                className="h-7 px-2 text-[11px]"
                                onClick={() => onOpenAppointment(nextAppointment.id)}
                            >
                                Открыть ближайшую
                            </Button>
                        )}
                    />
                    <SummaryCard
                        icon={<Activity className="h-4 w-4" />}
                        title="ЭКГ"
                        count={data.ecg.state === 'ready' ? data.ecg.studies.length : null}
                        value={lastStudy
                            ? `${lastStudy.statusLabel} · ЧСС ${lastStudy.measurements.hrBpm === null ? '—' : Math.round(lastStudy.measurements.hrBpm)}`
                            : 'Исследований нет'}
                        detail={lastStudy
                            ? `${formatValue(lastStudy.createdAt, 'date:dd.MM.yyyy HH:mm', 'timestamp')} · ${ECG_QUALITY_LABELS[lastStudy.qualityVerdict]}`
                            : ''}
                        note={data.ecg.state === 'ready'
                            ? ''
                            : data.ecg.state === 'denied'
                                ? 'Нет доступа'
                                : 'Раздел недоступен'}
                        actionLabel="История ЭКГ"
                        onOpen={() => onGoTab('ecg')}
                        extra={lastStudy && (
                            <Button
                                size="sm"
                                className="h-7 px-2 text-[11px]"
                                onClick={() => onOpenStudy(lastStudy.id)}
                            >
                                Открыть последнее
                            </Button>
                        )}
                    />
                </div>
            </section>

            <section className="rounded-lg border bg-card p-3">
                <div className="flex flex-wrap items-center gap-2 text-sm font-medium">
                    <Sparkles className="h-4 w-4 text-primary" />
                    Психология
                    <Button size="sm" variant="outline" className="ml-auto h-7 px-2 text-[11px]" onClick={() => onGoTab('psy')}>
                        Все тесты и заключения
                    </Button>
                </div>
                {data.psy.state === 'ready' ? (
                    <>
                        <div className="mt-1 text-xs text-muted-foreground">
                            результатов: {data.psy.results.length} · заключений: {data.psy.conclusions.length}
                        </div>
                        <div className="mt-1 line-clamp-3 whitespace-pre-wrap break-words text-xs">
                            {lastConclusion
                                ? lastConclusion.text || 'Текст заключения не заполнен.'
                                : lastResult
                                    ? `Последний тест: ${lastResult.methodName}`
                                    : 'Тестов и заключений нет.'}
                        </div>
                    </>
                ) : (
                    <div className="mt-1 text-xs text-muted-foreground">
                        {data.psy.state === 'denied' ? 'Нет доступа' : 'Психологические данные не ведутся в этой базе.'}
                    </div>
                )}
            </section>

            {(contactItems.length > 0 || patient.comment) && (
                <section className="space-y-2">
                    <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Контакты и документы</div>
                    {contactItems.length > 0 && (
                        <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">{contactItems}</div>
                    )}
                    {patient.comment && (
                        <div className="rounded-lg border bg-card px-3 py-2">
                            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Комментарий</div>
                            <div className="mt-0.5 whitespace-pre-wrap break-words text-sm">{patient.comment}</div>
                        </div>
                    )}
                </section>
            )}

            {/* История вызовов скорой (вертикаль «Скорая»): отбор по пациенту,
                клик — форма документа ambulance_calls. */}
            <PatientAmbulanceHistoryBlock history={data.ambulance} onOpenCall={onOpenCall} />

            {/* Профиль учётной записи (адрес/пол/год — ядро, entities): только
                ДОБАВЛЕНИЕ к сведениям карточки, поставочные формы не трогаются. */}
            <PatientProfileBlock linkedUserId={patient.linkedUserId} />
        </div>
    );
}
